import { useState } from 'react';
import type { ConfigBundle } from '@cmp/contracts';

import type { Draft } from '../../../lib/types';
import { Field, IconButton, Segmented as UiSegmented, input } from '../../../lib/ui';
import { verdict } from '../guardrails/contrast';
import { foreignFontHost } from '../guardrails/fontOrigin';
import { t } from '../../../lib/i18n';
import {
  addCategory,
  removeCategory,
  setButton,
  setButtonOrder,
  setPrefsButtonOrder,
  type Command,
  setCategory,
  setCategoryLabel,
  setEqualWeight,
  setFont,
  setGroup,
  setLayout,
  setLinks,
  setPrefsText,
  setReopen,
  setText,
  setTheme,
  type DraftStore,
} from '../store/draftStore';
import type { Selection } from './TreePanel';

const LAYOUTS = [
  { id: 'bar_bottom', label: 'Leiste unten', art: '▭▭▭' },
  { id: 'bar_top', label: 'Leiste oben', art: '▔▔▔' },
  { id: 'box_bottom_right', label: 'Box unten rechts', art: '  ▪' },
  { id: 'box_bottom_left', label: 'Box unten links', art: '▪  ' },
  { id: 'box_top_right', label: 'Box oben rechts', art: '  ▫' },
  { id: 'box_top_left', label: 'Box oben links', art: '▫  ' },
  { id: 'modal_center', label: 'Modal mittig', art: ' ▣ ' },
];

// The fallback stack: what draws while a house font is still in flight, and what draws
// forever on a device where the file never arrives. Web-safe only, because a stack that
// itself needed loading would not be a fallback.
// Value and the name a customer would recognise. Nobody picks a font by reading
// '"SF Mono", Menlo, monospace'.
const FONTS: [string, string][] = [
  ['system-ui, sans-serif', 'System — wie das Betriebssystem'],
  ['inherit', 'Wie die Website'],
  ['"Helvetica Neue", Arial, sans-serif', 'Grotesk — Helvetica, Arial'],
  ['Georgia, serif', 'Serif — Georgia'],
  ['"SF Mono", Menlo, monospace', 'Monospace'],
];

// Same shape the server enforces, checked while typing rather than at publish.
const FONT_OK = /^[\p{L}\p{N} ,'"_-]{1,200}$/u;
// A path on the site itself, or an explicit https URL. "//host/f.woff2" is deliberately
// not a path: it reads like one and is a request to somebody else.
const FONT_URL_OK = /^(\/[^/\s"'()][^\s"'()]*|https:\/\/[^\s"'()]+)$/;

// "accept" is deliberately not offered — see the note in the behaviour panel.
const DISMISS: [string, string][] = [
  ['close', 'nur schließen'],
  ['reject', 'alles ablehnen'],
  ['none', 'nichts tun'],
];

/** The effective value: the draft's own, else what the builder will apply. */
function eff(draft: Draft, bundle: ConfigBundle | null, path: string[]): any {
  const walk = (obj: any): any => path.reduce((n, k) => (n == null ? undefined : n[k]), obj);
  return walk(draft.ui) ?? walk(bundle?.ui ?? {});
}

/** The three the banner shows, in the order they default to. */
const BANNER_ORDER = ['reject_all', 'settings', 'accept_all'] as const;
/** And the three the preferences dialog shows — settings leads nowhere from inside it. */
const PREFS_ORDER = ['reject_all', 'save', 'accept_all'] as const;

/**
 * A stored order, normalised the same way the SDK normalises it: known keys in the order
 * given, then whatever is missing. A stored array that lost a key must not lose a button.
 */
const normalise = (stored: unknown, all: readonly string[]): string[] => {
  const want = Array.isArray(stored) ? (stored as string[]) : [];
  return [
    ...want.filter((k, n) => all.includes(k) && want.indexOf(k) === n),
    ...all.filter((k) => !want.includes(k)),
  ];
};

/**
 * The buttons by what they do, not by their wire key.
 *
 * This panel listed `accept_all` and `reject_all` in monospace — the database's names for
 * them, in a German console, on the screen where the wording of a consent dialog is
 * decided.
 */
const BUTTON_LABEL: Record<string, string> = {
  accept_all: 'Alle akzeptieren',
  reject_all: 'Alle ablehnen',
  settings: 'Einstellungen',
  save: 'Auswahl speichern',
};

const BUTTON_STYLE: Record<string, string> = {
  primary: 'Hervorgehoben',
  secondary: 'Umrandet',
  link: 'Als Link',
};

export function PropsPanel({
  store, draft, bundle, lang, selection, onSelect, domains,
}: {
  store: DraftStore;
  draft: Draft;
  bundle: ConfigBundle | null;
  lang: string;
  selection: Selection;
  onSelect: (s: Selection) => void;
  /** Every host this site answers on — what decides whether a font URL is a stranger. */
  domains: string[];
}) {
  const apply = store((s) => s.apply);
  const [newSlug, setNewSlug] = useState('');

  if (selection.kind === 'texts') {
    // Not `t`: that is the translation function now.
    const texts = draft.texts[lang] ?? {};
    const b = (bundle?.ui.texts ?? {}) as Record<string, string>;
    return (
      <Group title={t('Titel & Text · {lang}', { lang: lang.toUpperCase() })}>
        <Field label={t('Überschrift')}>
          <input className={input} value={texts.title ?? b['title'] ?? ''}
            onChange={(e) => apply(setText(lang, 'title', e.target.value))} />
        </Field>
        <Field label={t('Text')} hint={t('Dieser Wortlaut wird im Nachweis festgehalten.')}>
          <textarea className={input} rows={6} value={texts.body ?? b['body'] ?? ''}
            onChange={(e) => apply(setText(lang, 'body', e.target.value))} />
        </Field>
      </Group>
    );
  }

  if (selection.kind === 'layout') {
    const current = eff(draft, bundle, ['layout']) ?? 'box_bottom_right';
    return (
      <Group title={t('Layout & Position')}>
        <div className="grid grid-cols-2 gap-2">
          {LAYOUTS.map((l) => (
            <button
              key={l.id}
              onClick={() => apply(setLayout(l.id))}
              className={`rounded-lg border p-3 text-left text-xs transition ${
                current === l.id
                  ? 'border-[var(--color-brand)] bg-[var(--color-sunken)]'
                  : 'border-[var(--color-line)] hover:border-[var(--color-muted)]'
              }`}
            >
              <div className="mb-1 font-mono text-sm text-[var(--color-muted)]">{l.art}</div>
              {t(l.label)}
            </button>
          ))}
        </div>
      </Group>
    );
  }

  if (selection.kind === 'theme') {
    const fg = eff(draft, bundle, ['theme', 'fg']);
    const bg = eff(draft, bundle, ['theme', 'bg']);
    const onPrimary = eff(draft, bundle, ['theme', 'onPrimary']);
    const primary = eff(draft, bundle, ['theme', 'primary']);
    const body = verdict(fg, bg);
    const btn = verdict(onPrimary, primary);
    return (
      <Group title={t('Farben & Form')}>
        {(['primary', 'onPrimary', 'bg', 'fg', 'muted'] as const).map((k) => (
          <Field key={k} label={k}>
            <div className="flex gap-2">
              <input type="color" aria-label={k} className="h-9 w-11 rounded border border-[var(--color-line)]"
                value={/^#[0-9a-f]{6}$/i.test(eff(draft, bundle, ['theme', k]) ?? '') ? eff(draft, bundle, ['theme', k]) : '#000000'}
                onChange={(e) => apply(setTheme(k, e.target.value))} />
              <input className={input} value={eff(draft, bundle, ['theme', k]) ?? ''}
                onChange={(e) => apply(setTheme(k, e.target.value))} />
            </div>
          </Field>
        ))}

        {/* The ratio moves with the picker. Finding out after publish is how people end
            up choosing colours by trial and error. */}
        <div className="space-y-1 rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3 text-xs">
          <div className={body.passes ? 'text-[var(--color-live)]' : 'text-[var(--color-danger)]'}>Text auf Hintergrund: {body.text}</div>
          <div className={btn.passes ? 'text-[var(--color-live)]' : 'text-[var(--color-danger)]'}>Button-Beschriftung: {btn.text}</div>
        </div>

        <Field label={t('Overlay')} hint={t('Die Abdunklung hinter dem Dialog.')}>
          <input className={input} value={eff(draft, bundle, ['theme', 'overlay']) ?? ''}
            placeholder="rgba(0,0,0,.45)"
            onChange={(e) => apply(setTheme('overlay', e.target.value))} />
        </Field>
        <Slider label={t('Ecken-Radius')} min={0} max={28} value={Number(eff(draft, bundle, ['theme', 'radius']) ?? 12)}
          onChange={(v) => apply(setTheme('radius', v))} unit="px" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={eff(draft, bundle, ['theme', 'shadow']) !== false}
            onChange={(e) => apply(setTheme('shadow', e.target.checked))} />
          {t('Schatten')}
        </label>
      </Group>
    );
  }

  if (selection.kind === 'typo') {
    const font = eff(draft, bundle, ['theme', 'font']) ?? FONTS[0]![0];
    const known = FONTS.some(([v]) => v === font);
    const bad = font !== '' && !FONT_OK.test(font);
    return (
      <Group title={t('Schrift')}>
        <Field label={t('Schriftart')}>
          <select
            className={input}
            value={known ? font : '__own'}
            onChange={(e) => apply(setTheme('font', e.target.value === '__own' ? '' : e.target.value))}
          >
            {FONTS.map(([v, l]) => <option key={v} value={v}>{t(l!)}</option>)}
            <option value="__own">{t('Hausschrift …')}</option>
          </select>
        </Field>

        {!known && (
          <Field
            label={t('Hausschrift')}
            hint={t('Der Name der Familie, so wie Ihre Seite sie lädt. Immer eine Ausweichschrift dahinter — sie entscheidet, solange eine Schriftdatei unten noch lädt, und auf einem Gerät, wo diese nie ankommt.')}
            error={bad ? 'Nur ein Schriftname — keine Adresse, kein url(), kein @import.' : undefined}
          >
            <input
              className={input}
              value={font}
              placeholder='"Brand Sans", Helvetica, sans-serif'
              onChange={(e) => apply(setTheme('font', e.target.value))}
            />
          </Field>
        )}

        {/* Said once, plainly, where the decision is made. The banner is the one script on
            the page that must not touch a third party before the visitor has decided — a
            font from the customer's own site is not one, a font from anywhere else is. */}
        <p className="rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3 text-xs leading-snug text-[var(--color-muted)]">
          {t('Das Banner lädt eine Schrift nur von Ihrer eigenen Website. Von dort ist es dieselbe Herkunft wie die Seite selbst. Ein Webfont von Google Fonts oder einem fremden CDN wäre dagegen ein Aufruf bei einem Dritten, bevor eingewilligt wurde — genau das, was das Banner verhindert.')}
        </p>

        <HouseFont
          role="headline" label={t('Überschrift')} draft={draft} bundle={bundle}
          apply={apply} domains={domains}
        />
        <HouseFont
          role="body" label={t('Fließtext')} draft={draft} bundle={bundle}
          apply={apply} domains={domains}
        />

        <Slider label={t('Schriftgröße')} min={11} max={20} value={Number(eff(draft, bundle, ['theme', 'fontSize']) ?? 14)}
          onChange={(v) => apply(setTheme('fontSize', v))} unit="px" />
      </Group>
    );
  }

  if (selection.kind === 'buttons') {
    const equal = eff(draft, bundle, ['buttons', 'equal_weight']) !== false;
    const order = normalise(eff(draft, bundle, ['buttons', 'order']), BANNER_ORDER);
    const prefsOrder = normalise(eff(draft, bundle, ['buttons', 'prefs_order']), PREFS_ORDER);

    return (
      <Group title={t('Buttons')}>
        <Field
          label={t('Anordnung')}
          hint={t('„Untereinander“ macht jeden Button so breit wie der Dialog — auch im Auswahl-Dialog.')}
        >
          <Segmented
            value={eff(draft, bundle, ['box', 'button_layout']) ?? 'row'}
            options={[['row', 'nebeneinander'], ['column', 'untereinander'], ['split', 'außen verteilt']]}
            onChange={(v) => apply(setGroup('box', { button_layout: v }, 'Button-Anordnung geändert'))}
          />
        </Field>

        <Field
          label={t('Reihenfolge im Banner')}
          hint={'Die Reihenfolge im HTML ist die Vorlese- und Tab-Reihenfolge. Sie entscheidet, '
            + 'was ein Screenreader zuerst nennt — nicht nur, was links steht.'}
        >
          <OrderList order={order} onMove={(next) => apply(setButtonOrder(next))} />
        </Field>

        {/* The second layer had no control at all: its three buttons were hardcoded in a
            fixed order, so "Reihenfolge im Banner" was the only order anyone could set
            and the dialog quietly ignored it. */}
        <Field
          label={t('Reihenfolge im Auswahl-Dialog')}
          hint={t('Der Dialog hinter „Einstellungen“. „Auswahl speichern“ steht dort immer — es ist der einzige Weg, eine getroffene Auswahl festzuhalten.')}
        >
          <OrderList order={prefsOrder} onMove={(next) => apply(setPrefsButtonOrder(next))} />
        </Field>

        {(['accept_all', 'reject_all', 'settings', 'save'] as const).map((key) => {
          // Both the style and the colours hang off this, and so does the explanation.
          const locked = equal && (key === 'accept_all' || key === 'reject_all');
          return (
          <div key={key} className="rounded-[3px] border border-[var(--color-line)] p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="text-[13px] font-medium">{t(BUTTON_LABEL[key] ?? key)}</span>
              {key === 'save' ? (
                <span className="text-xs text-[var(--color-muted)]">{t('immer im Auswahl-Dialog')}</span>
              ) : (
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={eff(draft, bundle, ['buttons', key, 'show']) !== false}
                      onChange={(e) => apply(setButton(key, { show: e.target.checked }))} />
                    {t('im Banner')}
                  </label>
                  {/* Two layers, two answers. Someone who wants the dialog to be about
                      the per-category switches — with "Auswahl speichern" as the only
                      way out — should not have to take the same button off the banner,
                      where the first ask happens. `settings` has nowhere to lead from
                      inside the dialog, so it is not offered a second checkbox. */}
                  {key !== 'settings' && (
                    <label className="flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={eff(draft, bundle, ['buttons', key, 'hide_prefs']) !== true}
                        onChange={(e) => apply(setButton(key, { hide_prefs: !e.target.checked }))} />
                      {t('im Auswahl-Dialog')}
                    </label>
                  )}
                </span>
              )}
            </div>
            <input className={input} value={eff(draft, bundle, ['buttons', key, 'label']) ?? ''}
              onChange={(e) => apply(setButton(key, { label: e.target.value }))} />
            <select
              className={`${input} mt-2`}
              value={eff(draft, bundle, ['buttons', key, 'style']) ?? 'primary'}
              // Under equal weight the two decision buttons must share a style, so the
              // control that would break it is disabled rather than left to fail later.
              disabled={locked}
              onChange={(e) => apply(setButton(key, { style: e.target.value }))}
            >
              {Object.entries(BUTTON_STYLE).map(([v, label]) => (
                <option key={v} value={v}>{t(label)}</option>
              ))}
            </select>
            {/* Said out loud rather than left to a title attribute: a disabled control
                fires no hover events in most browsers, so the tooltip explaining why it
                is dead is the one thing you cannot reach from it. The checkbox that
                causes this sits below four cards, out of sight from here. */}
            {locked && (
              <p className="mt-1 text-xs text-[var(--color-muted)]">{t('Bei gleicher Gewichtung teilen sich Ablehnen und Akzeptieren Stil und Farbe.')}</p>
            )}

            {/* Locked together with the style under equal weight, and for the same reason:
                a colour is the easiest way to make one of two "identical" buttons quieter,
                so leaving it open would make the flag decorative. */}
            <ButtonColours
              draft={draft} bundle={bundle} apply={apply} k={key}
              locked={locked}
            />
          </div>
          );
        })}

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={equal} className="mt-1"
            onChange={(e) => {
              apply(setEqualWeight(e.target.checked));
              if (e.target.checked) {
                const style = eff(draft, bundle, ['buttons', 'accept_all', 'style']) ?? 'primary';
                apply(setButton('reject_all', { style }));
              }
            }} />
          <span>
            {t('Ablehnen gleich gewichtet wie Akzeptieren')}
            <span className="block text-xs text-[var(--color-muted)]">
              {t('Unter der DSGVO erwartet. Ohne diese Gleichbehandlung ist das Banner angreifbar.')}
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1"
            checked={eff(draft, bundle, ['box', 'button_full_width']) === true}
            onChange={(e) => apply(setGroup('box', { button_full_width: e.target.checked },
              'Buttonbreite geändert'))} />
          <span>
            {t('Buttons über die volle Breite')}
            <span className="block text-xs text-[var(--color-muted)]">
              {t('Nebeneinander teilen sie sich die Zeile zu gleichen Teilen, untereinander füllt jeder sie ganz. Gleiche Breite hält Ablehnen und Akzeptieren nebenbei auch optisch gleich.')}
            </span>
          </span>
        </label>
      </Group>
    );
  }

  if (selection.kind === 'badge') {
    const mode = eff(draft, bundle, ['reopen', 'mode']) ?? 'floating_badge';
    return (
      <Group title={t('Widerruf-Badge')}>
        <Field label={t('Modus')} hint={t('Der Widerruf muss so einfach sein wie die Einwilligung.')}>
          <select className={input} value={mode} onChange={(e) => apply(setReopen({ mode: e.target.value }))}>
            <option value="floating_badge">{t('Schwebendes Badge')}</option>
            <option value="none">{t('Keins (eigener Link nötig)')}</option>
          </select>
        </Field>
        {mode === 'floating_badge' && (
          <Field label={t('Position')}>
            <select className={input} value={eff(draft, bundle, ['reopen', 'position']) ?? 'bottom_left'}
              onChange={(e) => apply(setReopen({ position: e.target.value }))}>
              {['bottom_left', 'bottom_right', 'top_left', 'top_right'].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </Field>
        )}
        {mode === 'none' && (
          <p className="rounded-lg bg-[var(--color-note)] p-3 text-xs text-[var(--color-note-ink)]">
            {t('Ohne Badge müssen Sie selbst ein Element mit data-cmp-open platzieren,')}
            sonst kommen Besucher nicht mehr an ihre Auswahl.
          </p>
        )}
      </Group>
    );
  }

  if (selection.kind === 'links') {
    const links = draft.texts[lang]?.links ?? bundle?.ui.texts?.links ?? [];
    const write = (next: { label: string; url: string }[]) => apply(setLinks(lang, next));
    return (
      <Group title={t('Rechtliche Links · {lang}', { lang: lang.toUpperCase() })}>
        {links.map((l, i) => (
          <div key={i} className="space-y-2 rounded-lg border border-[var(--color-line)] p-3">
            <input className={input} value={l.label} placeholder="Datenschutz"
              onChange={(e) => write(links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            <input className={input} value={l.url} placeholder="/datenschutz"
              onChange={(e) => write(links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} />
            <button className="text-xs text-[var(--color-danger)] hover:underline"
              onClick={() => write(links.filter((_, j) => j !== i))}>
              {t('Entfernen')}
            </button>
          </div>
        ))}
        <button className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-sm"
          onClick={() => write([...links, { label: '', url: '' }])}>
          {t('Link hinzufügen')}
        </button>
        {links.length === 0 && (
          <p className="rounded-lg bg-[var(--color-note)] p-3 text-xs text-[var(--color-note-ink)]">
            {t('Ohne Link zur Datenschutzerklärung fehlt dem Banner die Information, auf die sich die Einwilligung bezieht.')}
          </p>
        )}
      </Group>
    );
  }

  if (selection.kind === 'logo') {
    const url = eff(draft, bundle, ['logo', 'url']) ?? '';
    return (
      <Group title={t('Logo')}>
        <Field label={t('Bild-URL')} hint="https:// oder ein Pfad auf der eigenen Seite.">
          <input className={input} value={url} placeholder="/logo.svg"
            onChange={(e) => apply(setGroup('logo', { url: e.target.value }, 'Logo geändert'))} />
        </Field>
        {/^http:\/\//i.test(url) && (
          // Mixed content is blocked by the browser, so this would be an invisible header
          // on exactly the https sites that care. The publish check refuses it too.
          <p className="rounded-lg bg-[var(--color-danger)]/8 p-3 text-xs text-[var(--color-danger)]">
            http:// wird auf https-Seiten blockiert. Veröffentlichen schlägt fehl.
          </p>
        )}
        <Field label={t('Alternativtext')} hint={t('Leer lassen, wenn das Logo rein dekorativ ist.')}>
          <input className={input} value={eff(draft, bundle, ['logo', 'alt']) ?? ''}
            onChange={(e) => apply(setGroup('logo', { alt: e.target.value }, 'Logo geändert'))} />
        </Field>
        <Slider label={t('Höhe')} min={16} max={72} value={Number(eff(draft, bundle, ['logo', 'height']) ?? 28)}
          onChange={(v) => apply(setGroup('logo', { height: v }, 'Logo geändert'))} unit="px" />
        <Field label={t('Ausrichtung')}>
          <Segmented
            value={eff(draft, bundle, ['logo', 'align']) ?? 'left'}
            options={[['left', 'links'], ['center', 'mittig'], ['right', 'rechts']]}
            onChange={(v) => apply(setGroup('logo', { align: v }, 'Logo geändert'))}
          />
        </Field>
        {url && (
          <button className="text-xs text-[var(--color-danger)] hover:underline"
            onClick={() => apply(setGroup('logo', { url: '' }, 'Logo entfernt'))}>
            {t('Logo entfernen')}
          </button>
        )}
      </Group>
    );
  }

  if (selection.kind === 'spacing') {
    const mobileWidth = Number(eff(draft, bundle, ['box', 'width_mobile']) ?? 0);
    return (
      <Group title={t('Größe & Abstände')}>
        <Slider label={t('Breite')} min={280} max={720} step={10}
          value={Number(eff(draft, bundle, ['box', 'width']) ?? 420)}
          onChange={(v) => apply(setGroup('box', { width: v }, 'Breite geändert'))} unit="px" />
        <p className="-mt-2 text-xs text-[var(--color-muted)]">
          {t('Gilt für Box und Modal. Leisten laufen immer über die volle Breite.')}
        </p>
        {/* Two states, not one slider with an invented default.
            The default below 640px is full width inside a 16px margin, and no number is
            stored for it. A slider alone had to show *some* value — it showed 640 — which
            claims a fixed width that does not exist and gives no way back to the default
            once dragged. So "full width" is its own option, and 0 is what records it:
            Go's omitempty drops a zero, so it round-trips as genuinely unset. */}
        <Field label={t('Breite auf Mobilgeräten')}>
          <Segmented
            value={mobileWidth > 0 ? 'fixed' : 'full'}
            options={[['full', 'volle Breite'], ['fixed', 'feste Breite']]}
            onChange={(v) => apply(setGroup('box',
              { width_mobile: v === 'full' ? 0 : 320 }, 'Mobile Breite geändert'))}
          />
        </Field>
        {mobileWidth > 0 && (
          <Slider label={t('Feste Breite auf Mobilgeräten')} min={260} max={620} step={10}
            value={mobileWidth}
            onChange={(v) => apply(setGroup('box', { width_mobile: v }, 'Mobile Breite geändert'))} unit="px" />
        )}
        <p className="-mt-2 text-xs text-[var(--color-muted)]">
          {mobileWidth > 0
            ? t('Unter 640 px Bildschirmbreite. Der Bildschirm begrenzt weiterhin — breiter als das Gerät wird das Banner nicht.')
            : t('Unter 640 px Bildschirmbreite füllt das Banner die Breite aus, mit 16 px Rand je Seite. Das ist die Voreinstellung.')}
        </p>
        <Slider label={t('Innenabstand')} min={8} max={40} value={Number(eff(draft, bundle, ['box', 'padding']) ?? 20)}
          onChange={(v) => apply(setGroup('box', { padding: v }, 'Innenabstand geändert'))} unit="px" />
        <Slider label={t('Abstand zwischen Elementen')} min={4} max={28}
          value={Number(eff(draft, bundle, ['box', 'gap']) ?? 12)}
          onChange={(v) => apply(setGroup('box', { gap: v }, 'Abstand geändert'))} unit="px" />
        <Field label={t('Button-Anordnung')}>
          <Segmented
            value={eff(draft, bundle, ['box', 'button_layout']) ?? 'row'}
            options={[['row', 'nebeneinander'], ['column', 'untereinander'], ['split', 'außen verteilt']]}
            onChange={(v) => apply(setGroup('box', { button_layout: v }, 'Button-Anordnung geändert'))}
          />
        </Field>
      </Group>
    );
  }

  if (selection.kind === 'behaviour') {
    const overlay = eff(draft, bundle, ['behaviour', 'overlay']);
    const blocking = eff(draft, bundle, ['behaviour', 'block_page']) === true;
    const closeShow = eff(draft, bundle, ['close', 'show']) === true;
    const rejectShow = eff(draft, bundle, ['buttons', 'reject_all', 'show']) !== false;
    const isModal = (eff(draft, bundle, ['layout']) ?? 'box_bottom_right') === 'modal_center';
    return (
      <Group title={t('Overlay & Schließen')}>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={overlay ?? isModal}
            onChange={(e) => apply(setGroup('behaviour', { overlay: e.target.checked }, 'Overlay geändert'))} />
          {t('Hintergrund abdunkeln')}
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={blocking}
            onChange={(e) => apply(setGroup('behaviour', { block_page: e.target.checked, overlay: true }, 'Seite sperren geändert'))} />
          <span>
            {t('Seite sperren, bis entschieden wurde')}
            <span className="block text-xs text-[var(--color-muted)]">
              {t('Ohne Sperre ist das Overlay nur Optik — Klicks gehen hindurch.')}
            </span>
          </span>
        </label>
        {blocking && !rejectShow && !closeShow && (
          // Blocking with no exit is a cookie wall: the only way in is to consent, which
          // is by definition not freely given. The publish check refuses it.
          <p className="rounded-lg bg-[var(--color-danger)]/8 p-3 text-xs text-[var(--color-danger)]">
            {t('Gesperrt, ohne Ablehnen-Button und ohne Schließen-Kreuz: Das ist eine Cookie-Wall. Veröffentlichen schlägt fehl.')}
          </p>
        )}

        <div className="space-y-3 rounded-lg border border-[var(--color-line)] p-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={closeShow}
              onChange={(e) => apply(setGroup('close', { show: e.target.checked, action: eff(draft, bundle, ['close', 'action']) ?? 'close' }, 'Schließen-Kreuz geändert'))} />
            {t('Schließen-Kreuz anzeigen')}
          </label>
          {closeShow && (
            <Field label={t('Ein Klick darauf bedeutet')}>
              <Segmented
                value={eff(draft, bundle, ['close', 'action']) ?? 'close'}
                options={[['close', 'nur schließen'], ['reject', 'alles ablehnen']]}
                onChange={(v) => apply(setGroup('close', { action: v }, 'Schließen-Aktion geändert'))}
              />
            </Field>
          )}
        </div>

        <Field label={t('Escape-Taste')}>
          <Segmented value={eff(draft, bundle, ['behaviour', 'esc']) ?? 'close'} options={DISMISS}
            onChange={(v) => apply(setGroup('behaviour', { esc: v }, 'Escape-Verhalten geändert'))} />
        </Field>
        <Field label={t('Klick auf den Hintergrund')}>
          <Segmented value={eff(draft, bundle, ['behaviour', 'overlay_click']) ?? 'none'} options={DISMISS}
            onChange={(v) => apply(setGroup('behaviour', { overlay_click: v }, 'Hintergrundklick geändert'))} />
        </Field>
        <p className="text-xs text-[var(--color-muted)]">
          „Zustimmen“ steht bewusst nicht zur Wahl: eine Wegklick-Geste als Einwilligung zu
          werten, ist genau das Muster, das hier nicht gebaut werden soll.
        </p>

        <Field label={t('Einblendung')}>
          <Segmented
            value={eff(draft, bundle, ['behaviour', 'animation']) ?? 'slide'}
            options={[['slide', 'einfahren'], ['fade', 'einblenden'], ['none', 'ohne']]}
            onChange={(v) => apply(setGroup('behaviour', { animation: v }, 'Einblendung geändert'))}
          />
        </Field>
      </Group>
    );
  }

  // ponytail: the placeholders below stay German. They are sample copy for the banner
  // text in the language currently being edited, not console chrome — so they should
  // follow `lang`, not the console. Sample wording per language when a second language
  // is actually in use.
  if (selection.kind === 'prefs') {
    // Not `t`: that is the translation function now.
    const texts = draft.texts[lang] ?? {};
    const showVendors = eff(draft, bundle, ['prefs', 'show_vendors']) === true;
    return (
      <Group title={t('Auswahl-Dialog · {lang}', { lang: lang.toUpperCase() })}>
        <Field label={t('Darstellung')}>
          <Segmented
            value={eff(draft, bundle, ['prefs', 'layout']) ?? 'center'}
            options={[['center', 'mittig'], ['sidebar_left', 'links'], ['sidebar_right', 'rechts']]}
            onChange={(v) => apply(setGroup('prefs', { layout: v }, 'Dialog-Darstellung geändert'))}
          />
        </Field>
        <Field label={t('Überschrift')}>
          <input className={input} placeholder="Datenschutz-Einstellungen" value={texts.prefs_title ?? ''}
            onChange={(e) => apply(setPrefsText(lang, 'prefs_title', e.target.value))} />
        </Field>
        <Field
          label={t('Einleitung')}
          hint={t('{consent_id} wird durch die Kennung dieser Einwilligung ersetzt — damit kann ein Besucher bei einer Auskunftsanfrage genau seinen Datensatz benennen.')}
        >
          <textarea className={input} rows={3} placeholder="Wählen Sie, welche Kategorien Sie erlauben möchten."
            value={texts.prefs_intro ?? ''}
            onChange={(e) => apply(setPrefsText(lang, 'prefs_intro', e.target.value))} />
        </Field>
        <Field label={t('Beschriftung für nicht abwählbare Kategorien')}>
          <input className={input} placeholder="Immer aktiv" value={texts.always_active ?? ''}
            onChange={(e) => apply(setPrefsText(lang, 'always_active', e.target.value))} />
        </Field>
        <Field label={t('Beschriftung des Schließen-Kreuzes')}>
          <input className={input} placeholder="Schließen" value={texts.close_label ?? ''}
            onChange={(e) => apply(setPrefsText(lang, 'close_label', e.target.value))} />
        </Field>

        <div className="space-y-2 rounded-lg border border-[var(--color-line)] p-3">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={showVendors}
              onChange={(e) => apply(setGroup('prefs', { show_vendors: e.target.checked }, 'Anbieterliste geändert'))} />
            <span>
              {t('Anbieter je Kategorie auflisten')}
              <span className="block text-xs text-[var(--color-muted)]">
                {t('{n} Anbieter gepflegt. Art. 13 verlangt, dass Empfänger benannt werden — im Dialog ist das der kürzeste Weg dorthin.', { n: (bundle?.vendors ?? []).length })}
              </span>
            </span>
          </label>
          {showVendors && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={eff(draft, bundle, ['prefs', 'show_cookies']) === true}
                onChange={(e) => apply(setGroup('prefs', { show_cookies: e.target.checked }, 'Cookie-Namen geändert'))} />
              {t('Auch die Cookie-Namen zeigen')}
            </label>
          )}
        </div>
      </Group>
    );
  }

  const cat = draft.categories.find((c) => c.slug === selection.slug);
  if (!cat) return <Group title={t('Kategorie')}><p className="text-sm text-[var(--color-muted)]">{t('Nicht gefunden.')}</p></Group>;

  return (
    <Group title={t('Kategorie · {slug}', { slug: cat.slug })}>
      <Field label={`Bezeichnung (${lang.toUpperCase()})`}>
        <input className={input} value={cat.labels[lang]?.label ?? ''}
          onChange={(e) => apply(setCategoryLabel(cat.slug, lang, 'label', e.target.value))} />
      </Field>
      <Field label={t('Beschreibung')}>
        <textarea className={input} rows={3} value={cat.labels[lang]?.desc ?? ''}
          onChange={(e) => apply(setCategoryLabel(cat.slug, lang, 'desc', e.target.value))} />
      </Field>

      {cat.required ? (
        // necessary is not a choice, so the controls that would pretend otherwise are
        // absent rather than disabled-and-confusing.
        <p className="rounded-[3px] border-l-2 border-[var(--color-line)] bg-[var(--color-paper)] p-3 text-xs text-[var(--color-muted)]">
          <strong>{t('Immer aktiv.')}</strong>{' '}
          {t('„necessary“ deckt ab, was für den Betrieb der Seite nötig ist, und ist per Gesetz nicht abwählbar.')}
        </p>
      ) : (
        <>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cat.default}
              onChange={(e) => apply(setCategory(cat.slug, { default: e.target.checked }))} />
            {t('vorausgewählt')}
          </label>
          {cat.default && (
            <p className="rounded-lg bg-[var(--color-note)] p-2 text-xs text-[var(--color-note-ink)]">
              {t('Vorausgewählt heißt: der Schalter steht im Dialog auf an. Unter Opt-in wird trotzdem nichts geladen, bevor jemand zustimmt.')}
            </p>
          )}
          <button className="text-xs text-[var(--color-danger)] hover:underline"
            onClick={() => { apply(removeCategory(cat.slug)); onSelect({ kind: 'texts' }); }}>
            {t('Kategorie entfernen')}
          </button>
        </>
      )}

      <div className="mt-4 border-t border-[var(--color-line)] pt-3">
        <Field label={t('Eigene Kategorie hinzufügen')} hint={t('Kleinbuchstaben, Ziffern, Unterstrich.')}>
          <div className="flex gap-2">
            <input className={input} value={newSlug} placeholder="ab_testing"
              onChange={(e) => setNewSlug(e.target.value)} />
            <button
              className="shrink-0 rounded-lg border border-[var(--color-line)] px-3 text-sm disabled:opacity-40"
              disabled={!/^[a-z][a-z0-9_]{2,31}$/.test(newSlug)}
              onClick={() => { apply(addCategory(newSlug, lang, newSlug)); setNewSlug(''); }}
            >
              {t('Hinzufügen')}
            </button>
          </div>
        </Field>
      </div>
    </Group>
  );
}

/**
 * One reorderable list of buttons.
 *
 * Arrows rather than drag-and-drop: three rows, and the arrows are the only version of
 * this that a keyboard reaches without a story about sensors and coordinate getters.
 */
function OrderList({ order, onMove }: { order: string[]; onMove: (next: string[]) => void }) {
  const name = (k: string): string => (BUTTON_LABEL[k] ? t(BUTTON_LABEL[k]!) : k);
  const move = (from: number, to: number): void => {
    const next = [...order];
    const [k] = next.splice(from, 1);
    next.splice(to, 0, k!);
    onMove(next);
  };
  return (
    <ul className="divide-y divide-[var(--color-line)] rounded-[3px] border border-[var(--color-line)]">
      {order.map((key, n) => (
        <li key={key} className="flex items-center justify-between gap-2 py-1 pl-3 pr-1">
          <span className="text-[13px]">{name(key)}</span>
          <span className="flex">
            <IconButton icon="up" label={t('{button} nach oben', { button: name(key) })}
              disabled={n === 0} onClick={() => move(n, n - 1)} />
            <IconButton icon="down" label={t('{button} nach unten', { button: name(key) })}
              disabled={n === order.length - 1} onClick={() => move(n, n + 1)} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <h3 className="border-b border-[var(--color-line)] pb-2 text-[13px] font-semibold">{title}</h3>
      {children}
    </div>
  );
}

/** Adapter onto the shared control, so this panel's tuples keep working. */
function Segmented({ value, options, onChange }: {
  value: string; options: [string, string][]; onChange: (v: string) => void;
}) {
  return (
    <UiSegmented
      size="sm"
      value={value}
      onChange={onChange}
      options={options.map(([v, label]) => ({ value: v, label: t(label) }))}
    />
  );
}

function Slider({ label, min, max, step, value, onChange, unit }: {
  label: string; min: number; max: number; step?: number;
  value: number; onChange: (v: number) => void; unit?: string;
}) {
  return (
    <label className="block">
      <span className="flex items-center justify-between text-sm font-medium">
        {label}
        <span className="text-xs text-[var(--color-muted)]">{value}{unit}</span>
      </span>
      <input type="range" min={min} max={max} step={step ?? 1} value={value} className="mt-1 w-full"
        onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}

/**
 * One house font: the family name to declare, and where to fetch it.
 *
 * Both or nothing — the server refuses half a face, because a family nobody loads falls
 * back invisibly and a URL nobody names is a request made for nothing.
 *
 * The warning is the part that matters. A relative path is same-origin on every host the
 * site answers on, so it is the form recommended in the hint; an absolute URL is
 * same-origin on exactly one, and on the others it is the third-party call this product
 * exists to prevent. It warns rather than blocks, because the domain list is what the
 * console knows and not necessarily everything the customer serves — the decision is
 * theirs, but it is not going to be made unknowingly.
 */
function HouseFont({ role, label, draft, bundle, apply, domains }: {
  role: 'headline' | 'body';
  label: string;
  draft: Draft;
  bundle: ConfigBundle | null;
  apply: (c: Command) => void;
  domains: string[];
}) {
  const family = eff(draft, bundle, ['fonts', role, 'family']) ?? '';
  const url = eff(draft, bundle, ['fonts', role, 'url']) ?? '';
  const foreign = url ? foreignFontHost(url, domains) : null;

  return (
    <div className="space-y-3 rounded-[3px] border border-[var(--color-line)] p-3">
      <div className="text-[13px] font-medium">{label}</div>
      <Field
        label={t('Name der Schriftfamilie')}
        error={family !== '' && !FONT_OK.test(family)
          ? t('Nur ein Schriftname — keine Adresse, kein url(), kein @import.') : undefined}
      >
        <input className={input} value={family} placeholder="Brand Sans"
          onChange={(e) => apply(setFont(role, { family: e.target.value }))} />
      </Field>
      <Field
        label={t('Adresse der Schriftdatei')}
        hint={t('Am besten ein Pfad ohne Domain: der ist auf jeder Adresse Ihrer Website dieselbe Herkunft — auch auf www und auf der Testumgebung.')}
        error={url !== '' && !FONT_URL_OK.test(url)
          ? t('Ein Pfad wie /fonts/brand.woff2 oder eine vollständige https-Adresse.') : undefined}
      >
        <input className={input} value={url} placeholder="/fonts/brand-sans.woff2"
          onChange={(e) => apply(setFont(role, { url: e.target.value }))} />
      </Field>

      {foreign && (
        <p className="rounded-[3px] bg-[var(--color-danger)]/8 p-3 text-xs leading-snug text-[var(--color-danger)]">
          {t('{host} gehört nicht zu den Domains dieser Website. Das Banner würde diese Schrift bei einem Dritten anfragen, bevor eingewilligt wurde. Ein Pfad wie /fonts/brand-sans.woff2 vermeidet das.', { host: foreign })}
        </p>
      )}
      {(family || url) && (family === '' || url === '') && (
        <p className="text-xs text-[var(--color-muted)]">
          {t('Beides nötig: ohne Namen wird die Datei nicht verwendet, ohne Adresse nicht geladen. Veröffentlichen schlägt sonst fehl.')}
        </p>
      )}
      {(family || url) && (
        <button className="text-xs text-[var(--color-danger)] hover:underline"
          onClick={() => apply(setFont(role, { family: '', url: '' }))}>
          {t('Schrift entfernen')}
        </button>
      )}
    </div>
  );
}

/**
 * Background and text colour for one button.
 *
 * Both or neither: the server refuses half a pair, because a background without a
 * foreground is a contrast accident waiting for the first theme change. "Aus" therefore
 * clears both and hands the button back to the theme.
 */
function ButtonColours({ draft, bundle, apply, k, locked }: {
  draft: Draft;
  bundle: ConfigBundle | null;
  apply: (c: Command) => void;
  k: string;
  locked: boolean;
}) {
  const bg = eff(draft, bundle, ['buttons', k, 'bg']) ?? '';
  const fg = eff(draft, bundle, ['buttons', k, 'fg']) ?? '';
  const on = bg !== '';
  const ratio = on ? verdict(fg, bg) : null;

  return (
    <div className="mt-2">
      {!on ? (
        <button
          className="text-xs text-[var(--color-brand)] hover:underline disabled:text-[var(--color-muted)] disabled:no-underline"
          disabled={locked}
          title={locked ? t('Bei gleicher Gewichtung teilen sich Ablehnen und Akzeptieren Stil und Farbe.') : undefined}
          onClick={() => apply(setButton(k, { bg: '#1d4ed8', fg: '#ffffff' }))}
        >
          {t('Eigene Farbe')}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {([['bg', 'Fläche'], ['fg', 'Schrift']] as const).map(([field, label]) => (
              <label key={field} className="flex items-center gap-1.5 text-xs">
                {t(label)}
                <input
                  type="color"
                  aria-label={`${t(label)} ${k}`}
                  disabled={locked}
                  className="h-7 w-9 rounded border border-[var(--color-line)]"
                  value={/^#[0-9a-f]{6}$/i.test(field === 'bg' ? bg : fg) ? (field === 'bg' ? bg : fg) : '#000000'}
                  onChange={(e) => apply(setButton(k, { [field]: e.target.value }))}
                />
              </label>
            ))}
            <button className="text-xs text-[var(--color-muted)] hover:underline"
              disabled={locked}
              onClick={() => apply(setButton(k, { bg: '', fg: '' }))}>
              {t('Aus')}
            </button>
          </div>
          {/* The ratio moves with the picker, because finding out at publish time is how
              people end up choosing colours by trial and error. */}
          {ratio && (
            <div className={`text-xs ${ratio.passes ? 'text-[var(--color-live)]' : 'text-[var(--color-danger)]'}`}>
              Kontrast: {ratio.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
